import { Inject } from '@odinjs/odin';

import { Endpoint, FormViewService, ProviderAccreditationPendenciesService, ToastService } from '@philips-emr/hpms-provider-commons';
import providerContext from '@philips-emr/hpms-provider-commons/lib/context/provider-context';
import { HpmsCustomCardEvent, HpmsCustomCardMenuRenderEvent, HPMSOnCreateMenu, HPMSOnOptionClick } from '@philips-emr/hpms-provider-components';
import { FetchAPI, FieldAPI, FormAPI, LegendsAPI, PanelAPI, ToastNotificationAPI } from '@philips-emr/tws-components';
import { Value } from '@philips-emr/tws-core';
import Controller, { OnAfterFetch, OnCancel, OnChange, OnMount, OnSubmit } from '@philips-emr/tws-core/lib/client';
import { FetchEvent, MountEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService, MessageService } from '@philips-emr/tws-core/lib/services';
import { isEmptyArrayOrObject } from '@philips-emr/tws-utils';

import { AccreditationBedsAliases as Aliases, FragmentNames } from '../../codegen';
import BaseController from '../BaseController';

import { AccreditationSteps } from './accreditation-update-controller';
import { ActionTypes } from './update-accreditation-type';

const localValidatorSUS = 'islowerOrEqualTheOtherValue';

interface Bed {
  nrSequence: number,
  ieBeds: string,
  dsBeds: string,
  qtBed: number,
  qtBedSUS: number
  ieAction: ActionTypes,
  dsAction: string,
}

const OptionKey = {
  EDIT: 'edit',
  DELETE: 'delete',
};

@Controller({ fragment: FragmentNames.ACCREDITATION_BEDS })
class AccreditationBedsController extends BaseController {

  @Inject(Aliases.FETCH_BED)
  private fetchBed: FetchAPI;
  @Inject(Aliases.FETCH_BEDS_LIST)
  private fetchBedsList: FetchAPI;
  @Inject(Aliases.FIELD_QUANTITY_SUS)
  private fieldQuantitySus: FieldAPI;
  @Inject(Aliases.FIELD_CARD)
  private fieldCards: FieldAPI;
  @Inject(Aliases.FORM_BED)
  private formBeds!: FormAPI;
  @Inject(Aliases.PANEL_PENDENCIES)
  private panelPendencies: PanelAPI;
  @Inject(Aliases.TOAST_NOTIFICATION)
  private toast: ToastNotificationAPI;
  @Inject(Aliases.FIELD_BEDS)
  private fieldBeds: FieldAPI;
  @Inject(Aliases.FIELD_AMOUNT)
  private fieldAmount: FieldAPI;
  @Inject(Aliases.LEGENDS)
  private legends: LegendsAPI;

  @OnMount(Aliases.LAYOUT_GENERAL_INFORMATION)
  onRootLayoutMount(): void {
    const { nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;
    this.context.set({ nrSeqProvider, nrSeqAccreditation });
    this.fetchBedsList.forceFetch();
  }

  @OnMount(Aliases.PANEL_FORM_BED)
  onMountPanelFormBed(event: MountEvent): void {
    const api = event.target as PanelAPI;
    FormViewService.shouldRenderForm(api, AccreditationSteps.BEDS);
  }

  @HPMSOnCreateMenu(Aliases.CARD_BED)
  onCreateCardBad(event: HpmsCustomCardMenuRenderEvent<Record<string, Value>>): void {
    const { data, shouldRender } = event;
    const { editable } = data;
    shouldRender(editable == 'S');
  }

  @OnMount(Aliases.FORM_BED)
  onMountForm(): void {
    this.fieldQuantitySus.addLocalValidator(localValidatorSUS, (quantitySUS) => {
      const quantity = this.formBeds.getRecord()['qtBeds'];
      const valid = (Number(quantitySUS ?? 0) <= Number(quantity ?? 0));
      return [
        {
          message: !valid ? 'provider.valid-amount-sus' : '',
          name: localValidatorSUS,
          valid,
        },
      ];
    });
  }

  @OnAfterFetch(Aliases.FETCH_BEDS_LIST)
  OnAfterFetchBedsList(event: FetchEvent): void {
    const data = event.data as unknown as [];
    const isEmpty = isEmptyArrayOrObject(data);
    this.fieldCards.setVisible(!isEmpty);
    if (isEmpty) {
      this.legends.setShowExpression('');
      this.legends.setHideExpression('');
    } else {
      this.legends.setShowExpression(MessageService.translate('tws.legends.show'));
      this.legends.setHideExpression(MessageService.translate('tws.legends.hide'));
    }
  }

  @OnChange(Aliases.FORM_BED)
  onChangeBedsForm(): void {
    this.formBeds.validate();
  }

  @OnSubmit(Aliases.FORM_BED)
  onSubmitFormBed(): void {
    if(this.formBeds.validate()) {
      const record = this.formBeds.getRecord() as unknown as Bed;
      const { nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
      const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;

      HttpService.post(Endpoint.POST.BEDS, { nrSeqProvider, nrSeqAccreditation }, { body: { ...record } })
        .then(() => {
          this.formBeds.setRecord({});
          this.showValidations(false);
          this.fieldCards.setVisible(true);
          this.fetchBedsList.forceFetch();
        })
        .catch((response) => {
          ToastService.open(response);
          this.showValidations(true);
        });
    }
  }

  private showValidations(validations: boolean): void {
    this.fieldAmount.setState({ showValidations: validations });
    this.fieldBeds.setState({ showValidations: validations });
    this.fieldQuantitySus.setState({ showValidations: validations });
  }

  @OnCancel(Aliases.FORM_BED)
  onCancelFormBed(): void {
    this.cleanForm();
    this.showValidations(false);
  }

  @HPMSOnOptionClick(Aliases.CARD_BED)
  onOptionClickHpmsCustomCardBed(event: HpmsCustomCardEvent<Bed>): void {
    const { data, key } = event;
    const { nrSequence } = data;
    const action = {
      [OptionKey.EDIT]: () => this.editRecord(nrSequence),
      [OptionKey.DELETE]: () => this.deleteRecord(nrSequence),
    };

    if (action[key]) {
      action[key]();
    }
  }

  @OnMount(Aliases.FETCH_PENDENCIES)
  onMountFetchPendencies(event: MountEvent): void {
    ProviderAccreditationPendenciesService.fetchCardPendencies(event, AccreditationSteps.BEDS);
  }

  @OnAfterFetch(Aliases.FETCH_PENDENCIES)
  onAfterFetch(event: FetchEvent): void {
    if (isEmptyArrayOrObject(event.data)) {
      this.panelPendencies.setVisible(false);
    } else {
      ProviderAccreditationPendenciesService.setMessageToastPendency(this.toast, event);
    }
  }

  private cleanForm(): void {
    this.formBeds.setRecord({});
    this.formBeds.setData({});
  }

  private editRecord(nrSequence: number): void {
    const { nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;
    this.fetchBed.setParameters({ nrSeqProvider, nrSeqAccreditation, nrSequence });
    this.fetchBed.forceFetch();
  }

  private deleteRecord(nrSequence: number): void {
    const { nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;

    HttpService.delete(Endpoint.DELETE.BEDS, { nrSeqProvider, nrSeqAccreditation, nrSequence })
      .then(() => {
        this.cleanForm();
        this.fetchBedsList.forceFetch();
      }).catch((response) => {
        ToastService.open(response);
      });
  }
}

export {
  AccreditationBedsController,
};
